// Pure arithmetic for the staff-side image cropper: what each Apple surface requires, whether a
// given source can satisfy it WITHOUT UPSCALING, what crop rectangle to cut, what output resolution
// to render, and whether the result actually conforms.
//
// Deliberately free of React and of `@firecms/*` imports, exactly like
// `apple_delegated_onboarding.utils.ts`. There is no test runner in this package, so the only way
// these numbers get exercised is by importing this module into a scratch script — which is why every
// decision below is a plain function over plain numbers and NOTHING here touches a canvas, a File or
// a DOM node.
//
// ⚠️ THE NUMBERS ARE THE BACKEND'S, NOT OURS. Every value in `SURFACE_RULES` is transcribed from the
// gate that will judge the uploaded file:
//   - cover photo min 1600x1040, max side 4864, aspect window [1.5380, 1.5402] on longest/shortest —
//     `delegated-brand-asset.utils.ts:126-133` + `media/cover-photo-aspect.ts:57-60`;
//   - place-card logo EXACTLY 1024x1024 — `delegated-brand-asset.utils.ts:134-138`;
//   - showcase creative 1:1, 492–4864 per side — `delegated-media/image-geometry.ts:68,71,185-191`.
//   - gallery photo 100–4864 per side, aspect anywhere in 1:2.2 … 2.2:1 —
//     `delegated-media/image-geometry.ts` (`LOCATION_GALLERY_MIN_SIDE_PX`,
//     `LOCATION_GALLERY_MAX_ASPECT`, `IMAGE_MAX_SIDE_PX`), enforced there by `checkLocationGallery`.
// If these drift from the backend, staff crop an image and then watch the drain refuse it, which is
// strictly worse than having no cropper. Change them only together with that repo.
//
// ⚠️ AND THIS DOES NOT REPLACE THAT GATE. The backend still re-fetches the bytes and re-checks the
// geometry itself. This module exists to move the refusal to the person who chose the photo, while
// they still have the original open.

import {
  AppleImageMimeType,
  AppleImageOutputMimeType,
  AppleImageSurface,
  AspectChoice,
  CropRect,
  ImageVerdict,
  PixelSize,
  UploadNames,
  UploadTypeVerdict,
} from "./apple_image_crop.types";

/* ------------------------------------------------------------------------------------------------
 * THE APPLE APERTURE — Apple's cover-photo tolerance, transcribed
 * ---------------------------------------------------------------------------------------------- */

/**
 * Apple's stated nominal cover-photo shape. Exported for MESSAGES only.
 *
 * ⚠️ Never compare a ratio against this directly. Apple's own documented minimum, 1600x1040, is
 * 1.53846…:1, so an exact test against 1.54 rejects the very size Apple prints as acceptable. Use
 * `isCoverPhotoAspect`.
 */
export const COVER_PHOTO_ASPECT_LABEL = "1.54:1";

/** Inclusive lower bound of Apple's documented tolerance (`AppleBrandAsset.txt:577-591`). */
export const COVER_PHOTO_ASPECT_MIN = 1.538;

/** Inclusive upper bound of Apple's documented tolerance (`AppleBrandAsset.txt:577-591`). */
export const COVER_PHOTO_ASPECT_MAX = 1.5402;

/**
 * Whether a ratio sits inside Apple's cover-photo window, inclusive at both ends.
 *
 * ⚠️ The caller passes LONGEST ÷ SHORTEST, matching the brand-asset gate
 * (`delegated-brand-asset.utils.ts:273-275`) rather than width ÷ height. A portrait cover photo is
 * therefore caught by the minimum-width rule, not by this — the same division of labour the backend
 * documents, kept identical on purpose.
 */
export const isCoverPhotoAspect = (ratio: number): boolean =>
  ratio >= COVER_PHOTO_ASPECT_MIN && ratio <= COVER_PHOTO_ASPECT_MAX;

/* ------------------------------------------------------------------------------------------------
 * THE SURFACE TABLE
 * ---------------------------------------------------------------------------------------------- */

/**
 * Why the cover photo's output is a MULTIPLE OF 20x13 rather than "width, then width ÷ 1.54".
 *
 * The output has to be whole pixels, and Apple's window is only 0.0022 wide. Deriving a height by
 * rounding lands outside it disturbingly easily: a 1600px-wide crop gives `round(1600 / 1.54)` =
 * 1039, which is BELOW Apple's 1040 minimum height, and 1600x1039 is 1.53994:1 — inside the window
 * by luck, refused by the minimum-height rule anyway.
 *
 * 1600x1040 reduces exactly to 20:13, and 20 ÷ 13 = 1.53846…, which sits inside Apple's window with
 * 0.00046 to spare on the low side. So every integer multiple of 20x13 is EXACTLY on Apple's own
 * documented shape, and the rounding problem disappears instead of being managed. `k` from 80
 * (1600x1040, Apple's minimum) to 243 (4860x3159, the largest whose long side clears 4864).
 */
const COVER_PHOTO_STEP: PixelSize = { width: 20, height: 13 };
const COVER_PHOTO_MIN_MULTIPLE = 80;
const COVER_PHOTO_MAX_MULTIPLE = 243;

/**
 * Apple's ceiling on any side, shared by every surface (`image-geometry.ts:68`).
 *
 * ⚠️ MOVED ABOVE `SURFACE_RULES` (it used to sit just below) because the gallery rule interpolates it
 * into its `requirement` sentence, and that template literal is evaluated when the table is
 * CONSTRUCTED at module load — a `const` referenced before its declaration is a TDZ `ReferenceError`,
 * not a hoisted `undefined`. The value and the export are unchanged.
 */
export const IMAGE_MAX_SIDE_PX = 4864;

/** Gallery photo: minimum side, matching the backend's `LOCATION_GALLERY_MIN_SIDE_PX`. */
const GALLERY_MIN_SIDE_PX = 100;

/**
 * Gallery photo: the widest (and, reciprocally, the tallest) shape Apple allows — 2.2:1 and 1:2.2,
 * matching the backend's `LOCATION_GALLERY_MAX_ASPECT`. Inclusive at both ends.
 */
const GALLERY_MAX_ASPECT = 2.2;

/**
 * Slack for binary floating point ONLY, when comparing against the gallery window's endpoints. The
 * same purpose (and the same reasoning) as `ASPECT_EPSILON` in the backend's `image-geometry.ts`:
 * `2200/1000` and the literal `2.2` are not guaranteed to be the same double, so without this an image
 * sized exactly on the boundary could be refused by one ulp. Far too small to admit any shape a human
 * would call out of range.
 */
const GALLERY_ASPECT_EPSILON = 1e-9;

/**
 * The crop shapes offered for a gallery photo.
 *
 * ⚠️ NONE OF THESE SITS ON APPLE'S BOUNDARY, AND THAT IS DELIBERATE. The widest is 2:1 and the tallest
 * 1:2, against a window of 2.2:1 … 1:2.2 — so the floor-to-whole-pixels the export performs can never
 * push a framed crop outside the window. Offering exactly 2.2 would reintroduce the rounding problem
 * the cover photo solves with 20x13 multiples, for no benefit: nobody needs the last 10% of width, and
 * `verifyOutput` would refuse it after the shot had been composed.
 */
const GALLERY_ASPECT_CHOICES: readonly AspectChoice[] = [
  { label: "1:1", aspect: 1 },
  { label: "4:3", aspect: 4 / 3 },
  { label: "3:2", aspect: 3 / 2 },
  { label: "16:9", aspect: 16 / 9 },
  { label: "2:1", aspect: 2 },
  { label: "3:4", aspect: 3 / 4 },
  { label: "2:3", aspect: 2 / 3 },
  { label: "1:2", aspect: 1 / 2 },
];

interface SurfaceRule {
  /** How this surface is named to staff. */
  label: string;
  /**
   * ⚠️ THE FILE-PICKER RESTRICTION (§4), applied as the `accept` attribute on the file input.
   *
   * A browser hint and nothing more — it can be defeated by dragging a file in or by a
   * picker that ignores it — so `sniffImageMimeType` re-checks the actual BYTES and
   * `evaluateUploadType` refuses with a stated reason. Two layers, because only the second is real.
   *
   * ⚠️ HEIC IS EXCLUDED HERE AND ONLY HERE. The backend's `ALLOWED_IMAGE_CONTENT_TYPES` still
   * includes `image/heic` and must NOT be narrowed: Apple accepts HEIC for a brand cover photo and
   * the backend serves callers beyond this CMS. Excluding it at this picker is a UI decision taken
   * because Apple's `convert-heic` endpoint is not being built in this version.
   */
  acceptedFiles: readonly AppleImageMimeType[];
  /**
   * The ratio the crop box uses, as width ÷ height.
   *
   * ⚠️ FOR THE THREE LOCKED SURFACES THIS IS THE ONLY SHAPE ALLOWED. For a surface that also declares
   * `cropAspectChoices` it is the INITIAL shape — the one the dialog opens on, and the one every
   * surface-only helper (`evaluateSource`, `maxZoomFor`) assumes unless handed an explicit aspect.
   * Kept non-optional so no existing call site changes.
   */
  cropAspect: number;
  /**
   * ⚠️ PRESENT ON GALLERY ONLY, AND ITS ABSENCE IS WHAT KEEPS THE OTHER THREE SURFACES LOCKED.
   *
   * Apple fixes a single ratio for a cover photo, a logo and a showcase creative, so those three must
   * stay aspect-locked — `cropAspectChoices` is undefined for them, the field renders no shape picker,
   * and `aspect` is `cropAspect` exactly as before. A gallery photo may be anything between 1:2.2 and
   * 2.2:1, so staff pick from presets that all fall inside that window.
   *
   * Presets rather than free-form dragging, for two reasons: `react-easy-crop`'s `aspect` prop is not
   * meaningfully optional (omitting it makes the crop box follow the container rather than becoming
   * genuinely free), and an unconstrained box would let a staff member frame 2.3:1 and only find out at
   * `verifyOutput` — after they had already composed the shot.
   */
  cropAspectChoices?: readonly AspectChoice[];
  /**
   * The smallest crop, in SOURCE pixels, that can produce a conforming output without upscaling.
   * A crop below this is refused rather than stretched — see the file header of the field component.
   */
  minCrop: PixelSize;
  /** Human-readable statement of Apple's rule, used in refusal copy and in the field's help text. */
  requirement: string;
}

const SURFACE_RULES: Record<AppleImageSurface, SurfaceRule> = {
  coverPhoto: {
    label: "cover photo",
    acceptedFiles: ["image/jpeg", "image/png"],
    cropAspect: COVER_PHOTO_STEP.width / COVER_PHOTO_STEP.height,
    minCrop: {
      width: COVER_PHOTO_STEP.width * COVER_PHOTO_MIN_MULTIPLE,
      height: COVER_PHOTO_STEP.height * COVER_PHOTO_MIN_MULTIPLE,
    },
    requirement: `at least 1600x1040, at most 4864 on the longest side, and ${COVER_PHOTO_ASPECT_LABEL} (Apple's tolerance is ${COVER_PHOTO_ASPECT_MIN}–${COVER_PHOTO_ASPECT_MAX} on longest ÷ shortest)`,
  },
  placecardLogo: {
    label: "place-card logo",
    // ⚠️ SVG belongs here. Apple accepts SVG for PLACECARD_LOGO — arguably the best format for a
    // logo — so a blanket JPEG/PNG list would exclude the format Apple PREFERS. An SVG bypasses the
    // cropper entirely; see `requiresCrop`.
    acceptedFiles: ["image/jpeg", "image/png", "image/svg+xml"],
    cropAspect: 1,
    // 1024 because the output is EXACTLY 1024x1024 and we will not upscale to reach it. A source
    // whose shorter side is under 1024 is refused, not stretched.
    minCrop: { width: 1024, height: 1024 },
    requirement: "exactly 1024x1024 (1:1) once cropped, so the source must be at least 1024x1024",
  },
  showcaseImage: {
    label: "showcase image",
    acceptedFiles: ["image/jpeg", "image/png"],
    cropAspect: 1,
    minCrop: { width: 492, height: 492 },
    requirement: "square (1:1), between 492 and 4864 pixels per side",
  },
  galleryPhoto: {
    label: "gallery photo",
    // ⚠️ JPEG/PNG ONLY HERE, AND THIS IS THE STRICTER OF TWO DISAGREEING SOURCES. The backend's
    // `image-geometry.ts` treats a non-JPEG gallery image as an ADVISORY, arguing that Apple documents
    // no format restriction for GALLERY beyond PREFERRING JPG — and it declines to invent one. The
    // 2026-08 gallery ticket brief states "File format: JPEG or PNG". The disagreement is resolved HERE
    // rather than there because this is the layer that can act on it: a staff member with the original
    // open can export a JPEG, whereas a backend refusal days later cannot. No SVG (a photograph is not
    // a vector) and no HEIC (this console cannot convert one — see `evaluateUploadType`).
    acceptedFiles: ["image/jpeg", "image/png"],
    // 4:3 is the INITIAL shape, not a rule: it is the most common camera aspect, it is comfortably
    // inside Apple's window, and staff can change it from the picker.
    cropAspect: 4 / 3,
    // 100 on BOTH sides, matching `LOCATION_GALLERY_MIN_SIDE_PX`. Aspect-independent, so this one
    // minimum is correct for every preset, portrait and landscape alike.
    minCrop: { width: GALLERY_MIN_SIDE_PX, height: GALLERY_MIN_SIDE_PX },
    requirement: `between ${GALLERY_MIN_SIDE_PX} and ${IMAGE_MAX_SIDE_PX} pixels per side, and any shape between 1:${GALLERY_MAX_ASPECT} (tall) and ${GALLERY_MAX_ASPECT}:1 (wide)`,
    cropAspectChoices: GALLERY_ASPECT_CHOICES,
  },
};

const SHOWCASE_MIN_SIDE_PX = 492;
const LOGO_EXACT_SIDE_PX = 1024;

/** Tolerance for comparing derived floating-point sizes. Far below one pixel. */
const EPSILON = 1e-6;

export const surfaceLabel = (surface: AppleImageSurface): string => SURFACE_RULES[surface].label;

/** The `accept` list for one surface's file input. See `SurfaceRule.acceptedFiles`. */
export const acceptedFilesFor = (surface: AppleImageSurface): readonly AppleImageMimeType[] =>
  SURFACE_RULES[surface].acceptedFiles;

/**
 * The crop box's INITIAL ratio, as width ÷ height — and for the three locked surfaces, its only one.
 * Unchanged for them: `SURFACE_RULES[surface].cropAspect` is exactly what it was.
 */
export const cropAspectFor = (surface: AppleImageSurface): number =>
  SURFACE_RULES[surface].cropAspect;

/**
 * The crop shapes staff may choose between on this surface, or an EMPTY array when Apple fixes a single
 * shape.
 *
 * ⚠️ EMPTY MEANS LOCKED, and the field renders no picker at all in that case — so the cover photo, the
 * logo and the showcase image behave exactly as they did before this surface existed. Only
 * `galleryPhoto` returns anything.
 */
export const cropAspectChoicesFor = (surface: AppleImageSurface): readonly AspectChoice[] =>
  SURFACE_RULES[surface].cropAspectChoices ?? [];

/** Apple's rule for one surface, as a sentence. */
export const requirementFor = (surface: AppleImageSurface): string =>
  SURFACE_RULES[surface].requirement;

/* ------------------------------------------------------------------------------------------------
 * FORMAT
 * ---------------------------------------------------------------------------------------------- */

const startsWith = (bytes: Uint8Array, signature: readonly number[]): boolean =>
  signature.every((byte, index) => bytes[index] === byte);

const ascii = (bytes: Uint8Array, from: number, to: number): string =>
  Array.from(bytes.slice(from, to), (byte) => String.fromCharCode(byte)).join("");

/**
 * ISO base-media brands that mean "this is a HEIF/HEIC still". Listed so HEIC gets its OWN refusal
 * sentence — "convert it" is actionable, "unsupported format" is not.
 */
const HEIF_BRANDS = new Set([
  "heic",
  "heix",
  "hevc",
  "hevx",
  "heim",
  "heis",
  "hevm",
  "hevs",
  "mif1",
  "msf1",
]);

/**
 * Identifies an image from its own BYTES, never from `File.type`.
 *
 * ⚠️ `File.type` is derived from the file EXTENSION by every browser, so a HEIC photo renamed to
 * `.jpg` arrives claiming `image/jpeg`. That is precisely the file this console must refuse, so the
 * claim is ignored and the header is read instead — the same decision the backend's
 * `sniffImageContentType` takes on the same bytes.
 *
 * Returns the sniffed type, `"image/heic"` for a HEIF still (so the caller can say "convert it"), or
 * `null` when the header matches nothing known.
 */
export const sniffImageMimeType = (bytes: Uint8Array): string | null => {
  if (bytes.length >= 3 && startsWith(bytes, [0xff, 0xd8, 0xff])) return "image/jpeg";
  if (bytes.length >= 8 && startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return "image/png";
  }
  if (bytes.length >= 6 && ascii(bytes, 0, 4) === "GIF8") return "image/gif";
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 12) === "WEBP") {
    return "image/webp";
  }
  if (bytes.length >= 12 && ascii(bytes, 4, 8) === "ftyp" && HEIF_BRANDS.has(ascii(bytes, 8, 12))) {
    return "image/heic";
  }
  // SVG is text, so there is no signature — look for a root `<svg` in the opening bytes. An XML
  // declaration or a comment may legitimately precede it, hence the window rather than a prefix test.
  const head = ascii(bytes, 0, Math.min(bytes.length, 1024));
  if (/<svg[\s>]/i.test(head)) return "image/svg+xml";
  return null;
};

/**
 * Whether the sniffed bytes are a format this surface accepts, with a stated reason when not.
 *
 * ⚠️ HEIC gets its own sentence on purpose. It is not a broken file — Apple itself accepts HEIC for a
 * brand cover photo — so the copy says what to do rather than implying the photo is unusable.
 */
export const evaluateUploadType = (
  surface: AppleImageSurface,
  sniffed: string | null,
): UploadTypeVerdict => {
  const rule = SURFACE_RULES[surface];
  const accepted = rule.acceptedFiles.join(", ");

  if (sniffed === null) {
    return {
      ok: false,
      reason: `This file's format could not be identified from its contents. The ${rule.label} accepts ${accepted}. Re-export it and try again.`,
    };
  }
  // Returns the type it CONFIRMED rather than a bare `true`, so the caller carries a narrowed
  // `AppleImageMimeType` onwards instead of casting a `string | null` it has merely been told is fine.
  if ((rule.acceptedFiles as readonly string[]).includes(sniffed)) {
    return { ok: true, contentType: sniffed as AppleImageMimeType };
  }

  if (sniffed === "image/heic") {
    return {
      ok: false,
      reason: `This is a HEIC photo (an iPhone default). Apple itself accepts HEIC, but this console does not convert it, so please export or convert the photo to JPEG or PNG first and attach that. Renaming the file to .jpg does NOT convert it.`,
    };
  }
  return {
    ok: false,
    reason: `This file is ${sniffed}. The ${rule.label} accepts ${accepted}.`,
  };
};

/**
 * Whether this file needs the cropper at all.
 *
 * ⚠️ AN SVG DOES NOT, AND THAT IS A DECISION. A vector logo has no intrinsic pixel size, so there is
 * nothing to crop and nothing to measure — "this SVG is 900x900" is not a meaningful statement. It
 * renders at 1024x1024 by definition when Apple rasterises it. The backend takes the identical
 * position and skips its own dimension check for SVG (`delegated-brand-asset.utils.ts:258`), so
 * measuring one here would invent a rule Apple does not have and refuse the format Apple prefers for
 * logos.
 */
export const requiresCrop = (sniffed: string): boolean => sniffed !== "image/svg+xml";

/**
 * The encoding a cropped file is written back as: THE SAME ONE IT ARRIVED IN.
 *
 * ⚠️ Silently re-encoding is a visible defect that passes every check. A PNG logo turned into a JPEG
 * loses its transparency — flattened onto black by a bare canvas — and a JPEG photo turned into a PNG
 * multiplies its size for no gain. Both surfaces accept either format, so there is no reason to
 * change it and every reason not to.
 */
export const outputMimeTypeFor = (sniffed: string): AppleImageOutputMimeType =>
  sniffed === "image/png" ? "image/png" : "image/jpeg";

const EXTENSIONS: Record<AppleImageMimeType, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  // SVG never passes through a canvas, but it still gets uploaded and still needs a correct
  // extension on both names — an SVG stored as `.png` is a file Apple's fetch will mis-handle.
  "image/svg+xml": "svg",
};

/** The file extension for a stored encoding, without the dot. */
export const extensionFor = (mimeType: AppleImageMimeType): string => EXTENSIONS[mimeType];

/* ------------------------------------------------------------------------------------------------
 * GEOMETRY — can this source conform, and to what
 * ---------------------------------------------------------------------------------------------- */

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * The largest rectangle of `aspect` that fits inside `source`, in source pixels.
 *
 * This is what the crop box covers at zoom 1, and therefore the best case available from this file.
 * If THIS is too small, no crop of this source can conform.
 */
export const largestCropForAspect = (aspect: number, source: PixelSize): PixelSize => {
  if (source.width <= 0 || source.height <= 0) return { width: 0, height: 0 };
  if (source.width / source.height >= aspect) {
    return { width: source.height * aspect, height: source.height };
  }
  return { width: source.width, height: source.width / aspect };
};

/**
 * Whether this SOURCE can yield a conforming image without upscaling — checked BEFORE the crop UI
 * opens, so a staff member is never invited to frame a photo that cannot be used.
 *
 * ⚠️ THE REFUSAL IS THE POINT. Upscaling to reach Apple's minimum produces a blurry image that passes
 * every arithmetic check here and then fails Apple's HUMAN quality review days later
 * (`ApplePhotoTextStandards` requires @1x resolution). Both numbers are in the message so the staff
 * member knows a bigger original is the fix.
 */
export const evaluateSource = (
  surface: AppleImageSurface,
  source: PixelSize,
  /**
   * The shape to test against. Defaults to the surface's own `cropAspect`, so every existing call site
   * is unchanged and the three locked surfaces cannot behave differently. Gallery passes the selected
   * preset when re-checking after a shape change.
   */
  aspect: number = SURFACE_RULES[surface].cropAspect,
): ImageVerdict => {
  const rule = SURFACE_RULES[surface];

  if (!source.width || !source.height || source.width < 0 || source.height < 0) {
    return {
      ok: false,
      reason: `This image's pixel dimensions could not be read, so it cannot be checked against Apple's rules for the ${rule.label}. Re-export it (a truncated or unusual encoding does this).`,
    };
  }

  const best = largestCropForAspect(aspect, source);
  if (best.width + EPSILON < rule.minCrop.width || best.height + EPSILON < rule.minCrop.height) {
    return {
      ok: false,
      reason:
        `This image is ${source.width}x${source.height}. Cropping it to the shape Apple requires for the ${rule.label} ` +
        `leaves at most ${Math.floor(best.width)}x${Math.floor(best.height)}, which is below the ` +
        `${rule.minCrop.width}x${rule.minCrop.height} minimum. Supply a larger original — this console will not ` +
        `enlarge the image to fit, because an enlarged photo passes Apple's size check and then fails its quality review.`,
    };
  }
  return { ok: true };
};

/**
 * Whether the crop the staff member actually framed is big enough.
 *
 * Separate from `evaluateSource` because a source can be plenty large while the crop box is zoomed
 * into a small part of it. `maxZoomFor` normally makes this unreachable; it is checked anyway,
 * because "normally unreachable" is not a guarantee and the alternative is a silent upscale.
 */
export const evaluateCrop = (surface: AppleImageSurface, crop: PixelSize): ImageVerdict => {
  const rule = SURFACE_RULES[surface];
  if (crop.width + EPSILON < rule.minCrop.width || crop.height + EPSILON < rule.minCrop.height) {
    return {
      ok: false,
      reason:
        `The selected area is ${Math.floor(crop.width)}x${Math.floor(crop.height)} pixels of the original, below the ` +
        `${rule.minCrop.width}x${rule.minCrop.height} Apple requires for the ${rule.label}. Zoom out and select more of the image.`,
    };
  }
  return { ok: true };
};

/**
 * The furthest the crop box may be zoomed in before the selection drops below Apple's minimum.
 *
 * Derived rather than fixed: on a 6000px source a staff member may zoom a long way, on a source only
 * just above the minimum they may not zoom at all. Capped at 3 to match `react-easy-crop`'s own
 * default ceiling and floored at 1 (a source that cannot reach 1 has already been refused by
 * `evaluateSource`).
 */
export const maxZoomFor = (
  surface: AppleImageSurface,
  source: PixelSize,
  /** Defaults to the surface's own `cropAspect` — see `evaluateSource` for why it is optional. */
  aspect: number = SURFACE_RULES[surface].cropAspect,
): number => {
  const rule = SURFACE_RULES[surface];
  const best = largestCropForAspect(aspect, source);
  if (best.width <= 0 || best.height <= 0) return 1;
  const headroom = Math.min(best.width / rule.minCrop.width, best.height / rule.minCrop.height);
  return clamp(headroom, 1, 3);
};

/**
 * The output resolution for a given crop — the size the canvas is rendered at.
 *
 * NEVER larger than the crop, so this cannot upscale: the cover photo takes the largest 20x13
 * multiple that fits, the square surfaces take the floor of the crop's shorter side. The place-card
 * logo is the exception and is always EXACTLY 1024x1024, which is a DOWNSCALE from a crop that
 * `evaluateCrop` has already confirmed is at least 1024 — Apple's "exactly 1024x1024" is not
 * satisfiable by cropping alone, so this surface crops to 1:1 and then scales.
 */
export const resolveOutputSize = (surface: AppleImageSurface, crop: PixelSize): PixelSize => {
  if (surface === "placecardLogo") {
    return { width: LOGO_EXACT_SIDE_PX, height: LOGO_EXACT_SIDE_PX };
  }
  if (surface === "coverPhoto") {
    const multiple = clamp(
      Math.floor(
        Math.min(crop.width / COVER_PHOTO_STEP.width, crop.height / COVER_PHOTO_STEP.height),
      ),
      COVER_PHOTO_MIN_MULTIPLE,
      COVER_PHOTO_MAX_MULTIPLE,
    );
    return {
      width: COVER_PHOTO_STEP.width * multiple,
      height: COVER_PHOTO_STEP.height * multiple,
    };
  }
  if (surface === "galleryPhoto") {
    // ⚠️ AN EXPLICIT BRANCH, PLACED BEFORE THE SQUARE FALL-THROUGH BELOW, AND THAT POSITION IS THE WHOLE
    // POINT. That fall-through returns `{ side, side }` for anything not `placecardLogo` and not
    // `coverPhoto` — so a new surface added to `AppleImageSurface` without a branch here is silently
    // exported as a SQUARE, with no compile error. (`verifyOutput` has the mirror-image trap: its
    // fall-through judges anything unenumerated against the cover photo's 1.538–1.5402 window.)
    //
    // A gallery photo has no target shape, so the output IS the crop, floored to whole pixels. The only
    // adjustment is Apple's 4864 ceiling, and it is applied PROPORTIONALLY: clamping each side
    // independently would distort the shape the staff member framed and could push the ratio outside
    // Apple's window. `minCrop` has already guaranteed both sides are at least 100.
    const width = Math.floor(crop.width);
    const height = Math.floor(crop.height);
    const longest = Math.max(width, height);
    if (longest <= IMAGE_MAX_SIDE_PX) return { width, height };
    const scale = IMAGE_MAX_SIDE_PX / longest;
    return {
      width: Math.max(GALLERY_MIN_SIDE_PX, Math.floor(width * scale)),
      height: Math.max(GALLERY_MIN_SIDE_PX, Math.floor(height * scale)),
    };
  }
  const side = clamp(
    Math.floor(Math.min(crop.width, crop.height)),
    SHOWCASE_MIN_SIDE_PX,
    IMAGE_MAX_SIDE_PX,
  );
  return { width: side, height: side };
};

/**
 * Rounds a crop rectangle to whole pixels and clamps it inside the source.
 *
 * ⚠️ `react-easy-crop` reports FRACTIONAL pixels unless `roundCropAreaPixels` is set, and a
 * fractional or over-hanging rectangle handed to `drawImage` silently samples transparent pixels
 * along an edge — a one-pixel border that nobody notices until the place card. Rounded and clamped
 * here rather than trusted.
 */
export const normalizeCropRect = (crop: CropRect, source: PixelSize): CropRect => {
  const maxX = Math.max(0, Math.floor(source.width) - 1);
  const maxY = Math.max(0, Math.floor(source.height) - 1);
  const x = clamp(Math.round(crop.x), 0, maxX);
  const y = clamp(Math.round(crop.y), 0, maxY);
  return {
    x,
    y,
    width: clamp(Math.round(crop.width), 1, Math.floor(source.width) - x),
    height: clamp(Math.round(crop.height), 1, Math.floor(source.height) - y),
  };
};

/**
 * THE LAST GATE BEFORE UPLOAD — re-checks a finished output against the SAME rules the backend will
 * apply, so a bug in the arithmetic above surfaces here as a refusal rather than as an Apple
 * rejection days later.
 *
 * Mirrors `delegated-brand-asset.utils.ts` `dimensionGaps` and `image-geometry.ts` `checkDimensions`.
 */
export const verifyOutput = (surface: AppleImageSurface, output: PixelSize): ImageVerdict => {
  const { width, height } = output;

  if (surface === "placecardLogo") {
    if (width !== LOGO_EXACT_SIDE_PX || height !== LOGO_EXACT_SIDE_PX) {
      return {
        ok: false,
        reason: `The cropped logo came out ${width}x${height}. Apple requires exactly ${LOGO_EXACT_SIDE_PX}x${LOGO_EXACT_SIDE_PX}. Nothing was uploaded.`,
      };
    }
    return { ok: true };
  }

  if (surface === "showcaseImage") {
    if (width !== height) {
      return {
        ok: false,
        reason: `The cropped image came out ${width}x${height}, which is not square. Apple requires 1:1 for a showcase image. Nothing was uploaded.`,
      };
    }
    if (width < SHOWCASE_MIN_SIDE_PX || width > IMAGE_MAX_SIDE_PX) {
      return {
        ok: false,
        reason: `The cropped image came out ${width}x${height}. Apple requires between ${SHOWCASE_MIN_SIDE_PX} and ${IMAGE_MAX_SIDE_PX} pixels per side. Nothing was uploaded.`,
      };
    }
    return { ok: true };
  }

  if (surface === "galleryPhoto") {
    // ⚠️ BEFORE THE COVER-PHOTO FALL-THROUGH BELOW, which judges any unenumerated surface against
    // 1.538–1.5402 — a window essentially no gallery photo satisfies, so omitting this branch would
    // refuse almost every upload with cover-photo copy.
    if (width < GALLERY_MIN_SIDE_PX || height < GALLERY_MIN_SIDE_PX) {
      return {
        ok: false,
        reason: `The cropped gallery photo came out ${width}x${height}. Apple's minimum is ${GALLERY_MIN_SIDE_PX} pixels on each side. Nothing was uploaded.`,
      };
    }
    if (Math.max(width, height) > IMAGE_MAX_SIDE_PX) {
      return {
        ok: false,
        reason: `The cropped gallery photo's longest side came out ${Math.max(width, height)}px. Apple's maximum is ${IMAGE_MAX_SIDE_PX}px. Nothing was uploaded.`,
      };
    }
    // ⚠️ WIDTH ÷ HEIGHT, NOT LONGEST ÷ SHORTEST, and the difference is real here where it is not on the
    // cover photo. Apple's gallery window is TWO-SIDED — 2.2:1 wide and 1:2.2 tall — so a portrait photo
    // has its own bound rather than being caught by a minimum-width rule. This matches the backend's
    // `checkLocationGallery`, which divides the same way.
    const ratio = width / height;
    if (
      ratio > GALLERY_MAX_ASPECT + GALLERY_ASPECT_EPSILON ||
      ratio < 1 / GALLERY_MAX_ASPECT - GALLERY_ASPECT_EPSILON
    ) {
      return {
        ok: false,
        reason: `The cropped gallery photo came out ${width}x${height}, an aspect ratio of ${ratio.toFixed(4)}:1. Apple requires anything between 1:${GALLERY_MAX_ASPECT} and ${GALLERY_MAX_ASPECT}:1. Nothing was uploaded.`,
      };
    }
    return { ok: true };
  }

  const rule = SURFACE_RULES.coverPhoto;
  if (width < rule.minCrop.width || height < rule.minCrop.height) {
    return {
      ok: false,
      reason: `The cropped cover photo came out ${width}x${height}. Apple's minimum is ${rule.minCrop.width}x${rule.minCrop.height}. Nothing was uploaded.`,
    };
  }
  if (Math.max(width, height) > IMAGE_MAX_SIDE_PX) {
    return {
      ok: false,
      reason: `The cropped cover photo's longest side came out ${Math.max(width, height)}px. Apple's maximum is ${IMAGE_MAX_SIDE_PX}px. Nothing was uploaded.`,
    };
  }
  const ratio = Math.max(width, height) / Math.min(width, height);
  if (!isCoverPhotoAspect(ratio)) {
    return {
      ok: false,
      reason: `The cropped cover photo came out ${width}x${height}, an aspect ratio of ${ratio.toFixed(4)}:1. Apple requires ${COVER_PHOTO_ASPECT_LABEL} — between ${COVER_PHOTO_ASPECT_MIN} and ${COVER_PHOTO_ASPECT_MAX}. Nothing was uploaded.`,
    };
  }
  return { ok: true };
};

/* ------------------------------------------------------------------------------------------------
 * NAMING AND PATHS
 * ---------------------------------------------------------------------------------------------- */

/** Where a surface's uploads live in Storage. One folder per surface, so the console is browsable. */
export const storagePathFor = (surface: AppleImageSurface): string =>
  `apple_onboarding_images/${surface}`;

const MAX_BASE_NAME_LENGTH = 64;

/**
 * Reduces an arbitrary uploaded name to a safe, recognisable base — no extension, no path, no
 * characters that need escaping in a URL or a Storage object name.
 *
 * Falls back to the surface's own name rather than to `blob` or `undefined`: the result is handed to
 * Apple verbatim and quoted back in failure messages, so "cover-photo" beats an empty string.
 */
export const safeBaseName = (originalName: string, surface: AppleImageSurface): string => {
  const withoutPath = originalName.split(/[\\/]/).pop() ?? "";
  const withoutExtension = withoutPath.replace(/\.[^.]+$/, "");
  const cleaned = withoutExtension
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_BASE_NAME_LENGTH)
    .replace(/-+$/g, "");
  return cleaned.length > 0 ? cleaned : surface.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
};

/**
 * The two names one upload needs: the human one that goes on the document and is handed to Apple, and
 * the unique one the Storage object takes.
 *
 * ⚠️ THEY DIFFER ON PURPOSE. Two clinics both attaching `logo.png` must not collide, because
 * overwriting the object would change what an EARLIER onboarding document's `imageUrl` resolves to —
 * silently swapping one client's logo for another's. `uniqueSuffix` is supplied by the caller (a
 * UUID) rather than generated here, so this function stays pure and testable.
 */
export const buildUploadNames = ({
  originalName,
  surface,
  mimeType,
  uniqueSuffix,
}: {
  originalName: string;
  surface: AppleImageSurface;
  /** The encoding as STORED — `image/svg+xml` on the logo's bypass path, otherwise the crop output. */
  mimeType: AppleImageMimeType;
  uniqueSuffix: string;
}): UploadNames => {
  const base = safeBaseName(originalName, surface);
  const extension = extensionFor(mimeType);
  return {
    documentFileName: `${base}.${extension}`,
    storageFileName: `${base}-${uniqueSuffix}.${extension}`,
  };
};

/**
 * The one-line note the field shows about what the export will do to the file, so a re-encode is
 * never silent. Both facts below are visible defects when they happen unannounced.
 */
export const describeExport = (
  surface: AppleImageSurface,
  sniffed: string,
  output: PixelSize,
): string => {
  const kept = outputMimeTypeFor(sniffed) === "image/png" ? "PNG" : "JPEG";
  const alpha =
    outputMimeTypeFor(sniffed) === "image/png"
      ? "Transparency is preserved."
      : "JPEG cannot store transparency, so any transparent area is flattened onto WHITE.";
  return `Will be saved as ${output.width}x${output.height} ${kept} — the same format you attached, not converted. ${alpha}`;
};
